import { AnalysisCriteriaAlert } from '@/features/analyses/components';
import type {
  FieldComponents,
  UploadedFile,
} from '@/shared/components/form-fields/types';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { toaster } from '@/shared/ui/toaster.component';
import { Button } from '@noorden-insurtech/alba-design-system';
import { ArrowLeft } from 'lucide-react';
import { useState } from 'react';
import { useProposalAnalysisRegisterObservation } from '../../hooks';
import { useProposalDecisionForm } from '../../hooks/use-proposal-decision-form.hook';
import { ProposalDecisionForm } from './proposal-decision-form.component';

const ACCEPT_TYPES =
  'application/pdf, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document, image/jpeg, image/png';

type ObservationFormValues = {
  observation: string;
  attachments: UploadedFile[];
};

interface Step2ObservationsProps {
  proposalId: string;
  underwritingMessages: string[];
  requiresInspection?: string;
  onBack: () => void;
}

type Mode = 'observations' | 'decision';

export function Step2Observations({
  proposalId,
  underwritingMessages,
  requiresInspection,
  onBack,
}: Step2ObservationsProps) {
  const [mode, setMode] = useState<Mode>('observations');

  const registerObservation =
    useProposalAnalysisRegisterObservation(proposalId);

  const observationForm = useProposalDecisionForm({
    defaultValues: {
      observation: '',
      attachments: [],
    } as ObservationFormValues,
    onSubmit: async ({ value }: { value: ObservationFormValues }) => {
      try {
        await registerObservation.mutateAsync({
          description: value.observation,
          attachments: value.attachments,
        });
        toaster.create({
          type: 'success',
          title: 'Observação registrada',
          description: 'A observação foi registrada com sucesso.',
        });
        observationForm.reset();
      } catch (error) {
        toaster.create({
          type: 'error',
          title: 'Erro ao registrar observação',
          description:
            error instanceof Error ? error.message : 'Erro inesperado',
        });
      }
    },
  });

  if (mode === 'decision') {
    return (
      <div className='flex flex-col gap-6'>
        <AnalysisCriteriaAlert messages={underwritingMessages} />
        <ProposalDecisionForm
          proposalId={proposalId}
          requiresInspection={requiresInspection}
          onCancel={() => setMode('observations')}
        />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-6'>
      <AnalysisCriteriaAlert messages={underwritingMessages} />

      <div className='flex flex-col gap-6 bg-white shadow-xl rounded-2xl py-12 px-8'>
        <observationForm.AppField
          name='observation'
          children={(field: FieldComponents) => (
            <field.TextareaField
              label='Observações'
              placeholder='Digite observações sobre a inspeção...'
              rows={5}
              maxLength={FIELD_MAX_LENGTHS.textarea}
            />
          )}
        />

        <observationForm.AppField
          name='attachments'
          children={(field: FieldComponents) => (
            <field.UploadField
              label='Anexos'
              accept={ACCEPT_TYPES}
              helperText='Formatos aceitos: PDF, DOC, DOCX, JPG'
              multiple
            />
          )}
        />
      </div>

      <div className='flex flex-row gap-4 justify-between items-center'>
        <Button variant='ghost' size='md' onClick={onBack}>
          <ArrowLeft className='size-4' />
          Voltar
        </Button>

        <div className='flex flex-row gap-3'>
          <observationForm.Subscribe
            selector={(state: {
              values: ObservationFormValues;
              isSubmitting: boolean;
            }) => ({
              hasObservation: !!state.values.observation?.trim(),
              isSubmitting: state.isSubmitting,
            })}
            children={({
              hasObservation,
              isSubmitting,
            }: {
              hasObservation: boolean;
              isSubmitting: boolean;
            }) => (
              <Button
                variant='secondary'
                size='md'
                onClick={observationForm.handleSubmit}
                disabled={!hasObservation || isSubmitting}
                loading={isSubmitting}
              >
                Registrar Observação
              </Button>
            )}
          />
          <Button
            variant='primary'
            size='md'
            onClick={() => setMode('decision')}
          >
            Ir para Decisão
          </Button>
        </div>
      </div>
    </div>
  );
}
